import styled from '@emotion/styled'
import { Link } from 'gatsby'
import React, { Fragment, useMemo } from 'react'

import SERVICES_DATA from '../../forms/enquiry-form/services-data.json'

const GLOBAL_NOTICES = [
  <Fragment key="legalServices">
    We will indicate likely fees based on your requirements.
  </Fragment>,
]

export const GlobalDisclaimer = () => (
  <div style={{ textAlign: 'center' }}>
    {GLOBAL_NOTICES.map((notice, i) => (
      <p key={`notice${i}`}>{notice}</p>
    ))}
    <PrivacyText>
      We value privacy and won't trade your data.
      <br /> See our <Link to="/privacy-policy/">Privacy Policy</Link> for
      details.
    </PrivacyText>
  </div>
)

export function buildNotices(
  enquiryType,
  includeGlobal,
  additionalNotices = [],
) {
  const category = SERVICES_DATA.categories.find(
    (cat) => cat.key === enquiryType.category,
  )
  const catNotices = category.notices || []
  const services = enquiryType.services || []

  const serviceNotices = services.reduce((acc, curr) => {
    const service = category.services.find(({ key }) => key === curr)
    // NOTE: 'other' service is automatically added and won't exist in category services
    if (!service || !service.notices) return acc
    return [...service.notices, ...acc]
  }, [])

  return [
    ...catNotices,
    ...serviceNotices,
    ...(includeGlobal ? GLOBAL_NOTICES : []),
    ...additionalNotices,
  ]
}

export const NoticesList = ({ notices }) => {
  if (notices.length < 1) return null
  return (
    <NoticesListUl>
      {notices.map((notice, i) => (
        <li key={`notice${i}`}>{notice}</li>
      ))}
    </NoticesListUl>
  )
}

const DefaultContainer = ({ children }) => (
  <NoticesContainer>{children}</NoticesContainer>
)

const Notices = ({
  enquiryType,
  includeGlobal = false,
  additionalNotices = [],
  noHeading = false,
  containerEl: Container = DefaultContainer,
  showPrivacyNotice = true,
}) => {
  const notices = useMemo(
    () => buildNotices(enquiryType, includeGlobal, additionalNotices),
    [enquiryType, includeGlobal, additionalNotices],
  )
  if (notices.length < 1) return null

  return (
    <Wrapper>
      <Container>
        {!noHeading && (
          <Heading className="heading heading--h5">
            Important &ndash; Please Note
          </Heading>
        )}
        <NoticesList notices={notices} />
        {showPrivacyNotice && (
          <PrivacyText>
            We value privacy and won't trade your data.
            <br /> See our <Link to="/privacy-policy/">Privacy Policy</Link> for
            details.
          </PrivacyText>
        )}
      </Container>
    </Wrapper>
  )
}

export default Notices

const NoticesListUl = styled.ul`
  font-size: inherit;
  padding-left: 20px;
  line-height: 1.3;
`

// Provides base styles
const Wrapper = styled.aside`
  text-align: left;
  font-size: 1.75rem;
  margin-top: 2.8rem;
`

const Heading = styled.h3`
  color: ${(p) => p.theme.colors.secondary};

  margin-block: 0 0.9em !important;
  font-size: inherit;
  font-weight: 600;
  color: ${(p) => p.theme.colors.alertIntense};
`

// Overridable
const NoticesContainer = styled.div`
  padding: 12px 16px 16px;
  color: ${(p) => p.theme.colors.darkTint};
  background: ${(p) => p.theme.colors.secondary}0a;
  border: 1px solid ${(p) => p.theme.colors.secondary}77;
  margin-bottom: 12px;
`

const PrivacyText = styled.p`
  text-align: center;
  text-wrap: balance;
  margin-bottom: 0;
  font-size: 0.9em;
  opacity: 0.9;
  margin-top: 1.5em;

  a {
    text-decoration-color: ${(p) => p.theme.colors.darkTint}33;
  }
`
